/**
 * Firebase Query Utilities
 * Works with REAL data structure in the database
 * 
 * Data Structure:
 * - users/ (top-level) - where users are stored with roles
 * - institutions/ - institution data
 * - institutions/{instId}/assignments/ - actual assignments
 * - institutions/{instId}/students/ - student subcollection
 * - institutions/{instId}/teachers/ - teacher subcollection
 */

import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    getDoc
} from 'firebase/firestore';
import { db } from '../firebaseconf';

// ============================================================
// GET USERS BY ROLE AND INSTITUTION
// ============================================================

export const getStudentsByInstitution = async (institutionId) => {
    try {
        const q = query(
            collection(db, 'users'),
            where('institutionId', '==', institutionId),
            where('role', '==', 'student')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            uid: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching students:', error);
        return [];
    }
};

export const getTeachersByInstitution = async (institutionId) => {
    try {
        const q = query(
            collection(db, 'users'),
            where('institutionId', '==', institutionId),
            where('role', '==', 'teacher')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            uid: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching teachers:', error);
        return [];
    }
};

export const getUsersByInstitutionAndRole = async (institutionId, role) => {
    try {
        const q = query(
            collection(db, 'users'),
            where('institutionId', '==', institutionId),
            where('role', '==', role)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            uid: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error(`Error fetching ${role}s:`, error);
        return [];
    }
};

// ============================================================
// GET USER DATA
// ============================================================

export const getUserByUID = async (uid) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return {
                uid: userDoc.id,
                ...userDoc.data()
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching user:', error);
        return null;
    }
};

export const getStudentData = async (uid, institutionId) => {
    try {
        const user = await getUserByUID(uid);
        if (!user) return null;
        
        return {
            id: uid,
            uid: uid,
            email: user.email,
            name: user.name,
            institutionId: user.institutionId,
            profileId: user.profileId,
            role: user.role,
            status: user.status,
            createdAt: user.createdAt
        };
    } catch (error) {
        console.error('Error fetching student data:', error);
        return null;
    }
};

export const getTeacherData = async (uid, institutionId) => {
    try {
        const user = await getUserByUID(uid);
        if (!user) return null;
        
        return {
            id: uid,
            uid: uid,
            email: user.email,
            name: user.name,
            institutionId: user.institutionId,
            profileId: user.profileId,
            role: user.role,
            status: user.status,
            createdAt: user.createdAt
        };
    } catch (error) {
        console.error('Error fetching teacher data:', error);
        return null;
    }
};

// ============================================================
// GET ASSIGNMENTS
// ============================================================

export const getAssignmentsByInstitution = async (institutionId) => {
    try {
        const assignmentsRef = collection(db, 'institutions', institutionId, 'assignments');
        const q = query(assignmentsRef, where('status', '==', 'active'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching assignments:', error);
        return [];
    }
};

export const getAssignmentsBySubject = async (institutionId, subject) => {
    try {
        const assignmentsRef = collection(db, 'institutions', institutionId, 'assignments');
        const q = query(
            assignmentsRef,
            where('subject', '==', subject),
            where('status', '==', 'active')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching assignments by subject:', error);
        return [];
    }
};

export const getAssignmentsByTeacher = async (institutionId, teacherId) => {
    try {
        const assignmentsRef = collection(db, 'institutions', institutionId, 'assignments');
        const q = query(
            assignmentsRef,
            where('teacher_id', '==', teacherId),
            where('status', '==', 'active')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching assignments by teacher:', error);
        return [];
    }
};

export const getAssignmentsByBranch = async (institutionId, branch) => {
    try {
        const assignmentsRef = collection(db, 'institutions', institutionId, 'assignments');
        const q = query(
            assignmentsRef,
            where('branch', '==', branch),
            where('status', '==', 'active')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching assignments by branch:', error);
        return [];
    }
};

// ============================================================
// GET INSTITUTION DATA
// ============================================================

export const getInstitution = async (institutionId) => {
    try {
        const instDoc = await getDoc(doc(db, 'institutions', institutionId));
        if (instDoc.exists()) {
            return {
                id: instDoc.id,
                ...instDoc.data()
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching institution:', error);
        return null;
    }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Extract branch from profileId
 * Example: "student_cse_001" -> "CSE"
 */
export const extractBranchFromProfileId = (profileId) => {
    if (!profileId) return 'General';
    const parts = profileId.split('_');
    if (parts.length >= 2) {
        return parts[1].toUpperCase();
    }
    return 'General';
};

/**
 * Extract role from profileId
 * Example: "student_cse_001" -> "student", "teacher_me_001" -> "teacher"
 */
export const extractRoleFromProfileId = (profileId) => {
    if (!profileId) return 'student';
    const parts = profileId.split('_');
    return parts[0].toLowerCase();
};

/**
 * Extract number from profileId
 * Example: "student_cse_001" -> "001", "teacher_me_001" -> "001"
 */
export const extractNumberFromProfileId = (profileId) => {
    if (!profileId) return '';
    const parts = profileId.split('_');
    return parts[parts.length - 1];
};

/**
 * Format user data for display
 */
export const formatUserData = (user) => {
    return {
        id: user.uid,
        uid: user.uid,
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || 'N/A',
        institutionId: user.institutionId,
        profileId: user.profileId,
        branch: extractBranchFromProfileId(user.profileId),
        status: user.status,
        createdAt: user.createdAt
    };
};

/**
 * Format assignment data for display
 */
export const formatAssignmentData = (assignment) => {
    return {
        id: assignment.id,
        title: assignment.title,
        description: assignment.description,
        subject: assignment.subject,
        branch: assignment.branch,
        dueDate: assignment.dueDate,
        totalMarks: assignment.total_marks,
        teacherId: assignment.teacher_id,
        teacherName: assignment.teacher_name,
        status: assignment.status,
        createdAt: assignment.createdAt,
        semester: assignment.semester
    };
};

/**
 * Get all branches from assignments or users
 */
export const getBranchesFromAssignments = (assignments) => {
    const branches = new Set();
    assignments.forEach(assign => {
        if (assign.branch) {
            branches.add(assign.branch.toUpperCase());
        }
    });
    return Array.from(branches);
};

/**
 * Filter assignments by branch
 */
export const filterAssignmentsByBranch = (assignments, branch) => {
    if (!branch || branch === 'all') return assignments;
    return assignments.filter(assign => 
        assign.branch && assign.branch.toLowerCase() === branch.toLowerCase()
    );
};

/**
 * Filter users by branch (from profileId)
 */
export const filterUsersByBranch = (users, branch) => {
    if (!branch || branch === 'all') return users;
    return users.filter(user => {
        const userBranch = extractBranchFromProfileId(user.profileId);
        return userBranch.toLowerCase() === branch.toLowerCase();
    });
};

// ============================================================
// BATCH OPERATIONS
// ============================================================

/**
 * Get complete data for dashboard
 */
export const getDashboardData = async (institutionId, userRole) => {
    try {
        const [institution, assignments] = await Promise.all([
            getInstitution(institutionId),
            getAssignmentsByInstitution(institutionId)
        ]);

        let additionalData = {};

        if (userRole === 'admin') {
            const [students, teachers] = await Promise.all([
                getStudentsByInstitution(institutionId),
                getTeachersByInstitution(institutionId)
            ]);
            additionalData = { students, teachers };
        } else if (userRole === 'teacher') {
            additionalData.students = await getStudentsByInstitution(institutionId);
        }

        return {
            institution,
            assignments,
            ...additionalData
        };
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        return {
            institution: null,
            assignments: [],
            students: [],
            teachers: []
        };
    }
};

const firebaseQueries = {
    getStudentsByInstitution,
    getTeachersByInstitution,
    getUsersByInstitutionAndRole,
    getUserByUID,
    getStudentData,
    getTeacherData,
    getAssignmentsByInstitution,
    getAssignmentsBySubject,
    getAssignmentsByTeacher,
    getAssignmentsByBranch,
    getInstitution,
    extractBranchFromProfileId,
    extractRoleFromProfileId,
    extractNumberFromProfileId,
    formatUserData,
    formatAssignmentData,
    getBranchesFromAssignments,
    filterAssignmentsByBranch,
    filterUsersByBranch,
    getDashboardData
};

export default firebaseQueries;
